import { Component, OnInit, AfterViewInit, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { AuthService } from '../../../core/auth/auth.service';
import { Router } from '@angular/router';
import {environment} from "../../../config/environment";
import { TranslateService } from '@ngx-translate/core';
import { LanguageService, LanguageOption, Language } from '../../../core/services/language.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, AfterViewInit {
  loading = false;
  error: string | null = null;
  hidePassword = true;

  form!: FormGroup;

  // Stats counters
  studentsCount = 0;
  teachersCount = 0;
  coursesCount = 0;

  // Target values
  private readonly targetStudents = 1000;
  private readonly targetTeachers = 10;
  private readonly targetCourses = 50;

  // Language selector
  isLanguageDropdownOpen: boolean = false;
  availableLanguages: LanguageOption[] = [];
  currentLanguage: LanguageOption;

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private translate: TranslateService,
    public languageService: LanguageService
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });
    this.availableLanguages = this.languageService.availableLanguages;
    this.currentLanguage = this.languageService.getCurrentLanguageOption();
  }

  ngOnInit() {}

  ngAfterViewInit() {
    // Only run animations in browser environment
    if (isPlatformBrowser(this.platformId)) {
      // Start counters with a delay to allow the page to load
      setTimeout(() => {
        this.animateCounter('students', this.targetStudents, 2000);
        this.animateCounter('teachers', this.targetTeachers, 1800);
        this.animateCounter('courses', this.targetCourses, 1500);
      }, 1000);
    }
  }

  private animateCounter(type: 'students' | 'teachers' | 'courses', target: number, duration: number) {
    // Double check browser environment
    if (!isPlatformBrowser(this.platformId)) {
      // Set final values immediately for SSR
      switch (type) {
        case 'students':
          this.studentsCount = target;
          break;
        case 'teachers':
          this.teachersCount = target;
          break;
        case 'courses':
          this.coursesCount = target;
          break;
      }
      return;
    }

    const startTime = performance.now();
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Use easing function for smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.floor(easeOutCubic * target);

      switch (type) {
        case 'students':
          this.studentsCount = currentValue;
          break;
        case 'teachers':
          this.teachersCount = currentValue;
          break;
        case 'courses':
          this.coursesCount = currentValue;
          break;
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  fillCredentials(email: string, password: string) {
    this.form.patchValue({ email, password });
    this.error = null;
  }

  goToHome() {
    this.router.navigate(['/']);
  }

  submit() {
    this.error = null;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const { email, password } = this.form.value as { email: string; password: string };
    this.loading = true;
    this.auth.login(email, password).subscribe({
      next: (res) => {
        // Store both token and user information
        this.auth.storeToken(res.accessToken);
        this.auth.storeUser(res.user);
        // Keep loading = true during navigation (component will be destroyed when new route loads)

        // Role-based navigation
        const userRole = res.user.role;
        switch (userRole) {
          case 'STUDENT':
            this.router.navigate(['/student']);
            break;
          case 'ADMIN':
          case 'TEACHER':
            this.router.navigate(['/admin/users']);
            break;
          case 'PARENT':
            // Future parent portal
            this.router.navigate(['/admin/users']);
            break;
          default:
            this.router.navigate(['/admin/users']);
            break;
        }
      },
      error: (err) => {
        this.loading = false;

        // Check if account is locked (HTTP 403 with "Account locked" title)
        if (err?.status === 403 && err?.error?.title === 'Account locked') {
          // Store user email and password for locked account page
          const lockedAccountInfo = {
            email: err.error.userDetails?.email || this.form.value.email,
            userId: err.error.userDetails?.userId,
            password: this.form.value.password
          };
          sessionStorage.setItem('locked_account_info', JSON.stringify(lockedAccountInfo));
          this.router.navigate(['/student/account-locked']);
          return;
        }

        // Handle other errors
        if (err?.error?.title) {
          this.error = err.error.title;
        } else if (err?.status === 401) {
          this.error = this.translate.instant('AUTH.ERRORS.INVALID_CREDENTIALS');
        } else {
          this.error = this.translate.instant('AUTH.ERRORS.LOGIN_FAILED');
        }
      },
    });
  }

  toggleLanguageDropdown(): void {
    this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen;
  }

  selectLanguage(lang: Language, event: Event): void {
    event.stopPropagation();
    this.languageService.setLanguage(lang);
    this.currentLanguage = this.languageService.getCurrentLanguageOption();
    this.isLanguageDropdownOpen = false;
  }

  getLanguageFlag(langCode: Language): string {
    const flags: Record<Language, string> = {
      'ar': '🇹🇳',
      'en': '🇬🇧',
      'fr': '🇫🇷'
    };
    return flags[langCode] || '🌐';
  }

  protected readonly environment = environment;
}
